import { useQueryClient } from "@tanstack/react-query";
import type React from "react";
import { useCallback, useState } from "react";

import type { DeleteEntityTextElements } from "~/components/DeleteEntityView";
import DeleteEntityView from "~/components/DeleteEntityView";
import { deleteInvoiceComment } from "~/modules/billing/api/invoiceComment/invoiceCommentApiDispatchers.ts";
import type { InvoiceCommentWithDate } from "~/modules/billing/api/invoiceComment/invoiceCommentTypes.ts";
import {
	ALL_PROCESSED_INVOICES_QUERY_KEY,
	INVOICE_QUERY_KEY,
	PROJECTS_INVOICES_QUERY_KEY,
} from "~/modules/billing/api/processedInvoice/processedInvoiceQueries.ts";
import { nl2br } from "~/utils/stringUtils.tsx";

type DeleteOrderModalProps = {
	comment?: InvoiceCommentWithDate;
	invoiceId: string;
	isOpen: boolean;
	onClose: () => void;
	projectId: string | null;
};

const DeleteInvoiceCommentModal: React.FunctionComponent<DeleteOrderModalProps> = ({
	comment,
	isOpen,
	invoiceId,
	onClose,
	projectId
}) => {
	const [busy, setBusy] = useState(false);
	const [serverErrorMessage, setServerErrorMessage] = useState("");

	const queryClient = useQueryClient();

	const handleConfirmationClick = async () => {
		if (!comment) return;
		try {
			setBusy(true);
			await deleteInvoiceComment(invoiceId, comment.id);
			await queryClient.invalidateQueries({ queryKey: INVOICE_QUERY_KEY(invoiceId) });
			queryClient.invalidateQueries({ queryKey: ALL_PROCESSED_INVOICES_QUERY_KEY });
			if (projectId) {
				queryClient.invalidateQueries({ queryKey: PROJECTS_INVOICES_QUERY_KEY(projectId) });
			}
			handleClose();
		} catch (error) {
			console.log(error);
			setServerErrorMessage("Ein unbekannter Fehler ist aufgetreten.");
		}
		setBusy(false);
	};

	const handleClose = useCallback(() => {
		setBusy(false);
		setServerErrorMessage("");
		onClose();
	}, [onClose]);

	const texts: DeleteEntityTextElements = {
		headerText: `Rechnungskommentar löschen`,
		bodyText: `Möchtest Du diesen Kommentar wirklich löschen?`,
		deleteButtonText: `Kommentar löschen`,
	};

	return (
		<DeleteEntityView
			isOpen={isOpen && !!comment}
			error={serverErrorMessage}
			texts={texts}
			onCloseClick={handleClose}
			busy={busy}
			handleConfirmationClick={handleConfirmationClick}
		>
			{comment && <div className="group relative mb-4 w-full rounded-lg bg-gray-200 p-2 shadow">
				<div className="max-h-36 overflow-y-auto rounded-md bg-white p-4 text-sm">
					{nl2br(comment.text)}
				</div>
			</div>}
		</DeleteEntityView>
	);
};

export default DeleteInvoiceCommentModal;
